import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import LabeledCheckbox from "../../shared-components/LabeledCheckbox";
import { FilterCount, FilterCountChip } from "./sidebarFilterCount";

export type SidebarFilterChild = {
  id: string;
  label: string;
  value: string;
  count: number;
  baselineCount?: number;
};

export type SidebarFilterGroup = {
  id: string;
  name: string;
  children: SidebarFilterChild[];
};

export function sumFilterCounts(
  children: Pick<SidebarFilterChild, "count">[]
): number {
  return children.reduce((total, child) => total + child.count, 0);
}

type Props = {
  groups: SidebarFilterGroup[];
  selectedValues: string[];
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    values: string[]
  ) => void;
};

export const SidebarGroupedFilters = ({
  groups,
  selectedValues,
  onChange,
}: Props) => {
  const selected = new Set(selectedValues.map((value) => value.toLowerCase()));
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<string>>(
    () => new Set()
  );

  const toggleGroup = (groupId: string) => {
    setCollapsedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  return (
    <div>
      {groups.map((group) => {
        const values = group.children.map((child) => child.value);
        const isChecked =
          values.length > 0 &&
          values.every((value) => selected.has(value.toLowerCase()));
        const isOpen = !collapsedGroupIds.has(group.id);

        const currentTotal = sumFilterCounts(group.children);
        const baselineTotal = sumFilterCounts(
          group.children.map((child) => ({
            count: child.baselineCount ?? child.count,
          }))
        );
        const showTotal = group.children.some(
          (child) => child.baselineCount !== undefined
        );

        return (
          <div key={group.id} className="mb-2">
            <div className="flex gap-1 items-start text-sm font-semibold">
              <LabeledCheckbox
                classes="mt-0.5 shrink-0"
                name={`group-${group.id}`}
                checked={isChecked}
                onChange={(event) => onChange(event, values)}
              />
              <span className="min-w-0 break-words">{group.name}</span>
              <FilterCountChip
                current={currentTotal}
                total={baselineTotal}
                showTotal={showTotal}
              />
              <button
                type="button"
                className="ml-auto mr-4 flex h-5 w-5 shrink-0 items-center justify-center text-gray-400"
                aria-expanded={isOpen}
                aria-label={`${isOpen ? "Collapse" : "Expand"} ${group.name}`}
                onClick={() => toggleGroup(group.id)}
              >
                {isOpen ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {isOpen && (
              <div className="ml-5 mt-1">
                {group.children.map((child) => (
                  <div
                    key={child.id}
                    className="flex gap-1 items-start text-sm text-gray-500 mb-1"
                  >
                    <LabeledCheckbox
                      classes="mt-0.5 shrink-0"
                      name={child.id}
                      checked={selected.has(child.value.toLowerCase())}
                      onChange={(event) => onChange(event, [child.value])}
                    />
                    <span>
                      {child.label}{" "}
                      <FilterCount
                        current={child.count}
                        total={child.baselineCount ?? child.count}
                        showTotal={child.baselineCount !== undefined}
                      />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
